import axios from "axios";
import moment from "moment";
import ReactPaginate from "react-paginate";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import WeekTotalDisplay from "./helpers/WeekTotalDisplay.jsx";
import ThisWeekTotalDisplay from "./helpers/ThisWeekTotalDisplay.jsx";
import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import {
  useTimerDataImportDataCalendar,
} from "./TimerDataImportDataApi.jsx";
import TimeCardImportDataCalendar from "./trackerImportDataModules/CardImportDataCalendar.jsx";
import cancelImage from "../../../../images/singleinvoice/cancel.svg";
import tick from "../../../../images/icons/tick-arrow.svg";
import Calendar from "../../../../helpers/dateComponents/Calendar.jsx";

const TrackerImportDataCalendar = (props) => {
  const isFromCalendar = props.isFromCalendar || false;
  const initialDateRange = props.initialDateRange || null;

  // Only keep states that are actually used in the component
  const [selectedPage, setSelectedPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [insertionLoading, setInsertionLoading] = useState(false);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [showingDropdownOpen, setShowingDropdownOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [invalidEntryIds, setInvalidEntryIds] = useState(new Set());
  const [editedId, setEditedId] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    // If coming from calendar import with initial date range, use it
    if (isFromCalendar && initialDateRange && initialDateRange.startDate && initialDateRange.endDate) {
      return [
        new Date(initialDateRange.startDate),
        new Date(initialDateRange.endDate)
      ];
    }
    // Otherwise use default (first day of month to today)
    const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const today = moment().tz(global.config.getTimeZone() || 'UTC').startOf("day").toDate();
    return [firstDay, today];
  });

  // Refs
  const componentRef = useRef(null);
  const showingDropdownRef = useRef(null);
  const calendarRef = useRef(null);
  const updateFormRef = useRef(null);

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 , scale: 0.97},
    visible: { opacity: 1 , scale: 1},
  };

  // Redux selectors
  const { appSettings } = useSelector((state) => state.appSettings);
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const selectedUser = user?._id;

  const {
    data: timer = [],
    isLoading: isTimerDataLoading,
    refetch: refetchTimerData,
  } = useTimerDataImportDataCalendar(token);

  // Calculate count of selected entries
  const selectedEntriesCount = React.useMemo(() => {
    let count = 0;
    timer.forEach((dateGroup) => {
      if (Array.isArray(dateGroup) && dateGroup.length === 2 && Array.isArray(dateGroup[1])) {
        dateGroup[1].forEach((entry) => {
          if (entry && entry._id && entry.isSelected !== false) {
            count++;
          }
        });
      }
    });
    return count;
  }, [timer]);

  // Data is always fetched fresh from DB on mount (refetchOnMount: 'always')
  // Cache is invalidated/removed in Calendar before navigation
  // No need for additional refetch here - query will automatically fetch fresh data


  const handleInsertAndClear = async () => {
    try {
      setInsertionLoading(true);

      // Validate timer data before sending to API
      if (!timer || !Array.isArray(timer) || timer.length === 0) {
        toast.error("No data available to import. Please ensure events are imported from calendar.", {
          position: "bottom-right",
          autoClose: 3000,
        });
        setInsertionLoading(false);
        return;
      }

      // Flatten the timer data structure: timer is [[dateString, entries], ...]
      // We need to extract all entries from all date groups, but only those with isSelected === true
      const allEntries = [];
      timer.forEach((dateGroup) => {
        if (Array.isArray(dateGroup) && dateGroup.length === 2 && Array.isArray(dateGroup[1])) {
          // dateGroup[1] contains the array of entries for that date
          dateGroup[1].forEach((entry) => {
            if (entry && entry._id) {
              // Only include entries where isSelected is true (default to true if undefined)
              if (entry.isSelected !== false) {
                allEntries.push(entry);
              }
            }
          });
        }
      });

      if (allEntries.length === 0) {
        toast.error("No selected entries found to import. Please select at least one entry.", {
          position: "bottom-right",
          autoClose: 3000,
        });
        setInsertionLoading(false);
        return;
      }

      // Comprehensive validation - only for selected entries
      const validationErrors = [];
      const invalidEntries = [];

      allEntries.forEach((item, index) => {
        const entryErrors = [];

        // Convert date strings to Date objects if needed
        let startTime = item.startTime;
        let endTime = item.endTime;
        let date = item.date;

        if (typeof startTime === 'string') {
          startTime = new Date(startTime);
        }
        if (typeof endTime === 'string') {
          endTime = new Date(endTime);
        }
        if (typeof date === 'string') {
          date = new Date(date);
        }

        // Duration validation
        const duration = typeof item.duration === 'string' ? parseInt(item.duration, 10) : item.duration;
        if (!duration || typeof duration !== 'number' || duration <= 0 || isNaN(duration)) {
          entryErrors.push(`Invalid or missing duration`);
        }

        // StartTime and EndTime validation
        if (!startTime || !(startTime instanceof Date) || isNaN(startTime.getTime())) {
          entryErrors.push(`Invalid or missing start time`);
        }

        if (!endTime || !(endTime instanceof Date) || isNaN(endTime.getTime())) {
          entryErrors.push(`Invalid or missing end time`);
        }

        if (startTime && endTime && endTime <= startTime) {
          entryErrors.push(`End time must be after start time`);
        }

        // Duration consistency check
        if (startTime && endTime && duration) {
          const calculatedDuration = Math.floor((endTime - startTime) / 1000);
          if (Math.abs(duration - calculatedDuration) > 1) {
            entryErrors.push(`Duration mismatch (calculated: ${calculatedDuration}s, stored: ${duration}s)`);
          }
        }

        // User validation
        const userId = typeof item.user === 'object' ? item.user._id : item.user;
        if (!userId || (typeof userId !== 'string' && typeof userId !== 'object')) {
          entryErrors.push(`Invalid or missing user`);
        }

        // Date validation
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
          entryErrors.push(`Invalid or missing date`);
        }

        // Task validation
        if (item.task === undefined || item.task === null) {
          entryErrors.push(`Missing task name`);
        }

        // Billable validation
        if (item.billable === undefined || item.billable === null) {
          entryErrors.push(`Missing billable status`);
        }

        // Project validation (required)
        const projectId = typeof item.project === 'object' && item.project !== null ? item.project._id : item.project;
        if (!projectId || projectId === null || projectId === undefined) {
          entryErrors.push(`Missing project (required)`);
        } else if (typeof projectId === 'string' && projectId.trim() === '') {
          entryErrors.push(`Empty project ID`);
        }

        // ClientId validation (required)
        const clientId = typeof item.clientId === 'object' && item.clientId !== null ? item.clientId._id : item.clientId;
        if (!clientId || clientId === null || clientId === undefined) {
          entryErrors.push(`Missing client ID (required)`);
        } else if (typeof clientId === 'string' && clientId.trim() === '') {
          entryErrors.push(`Empty client ID`);
        }

        // Sprint validation (optional but if exists, should be valid)
        if (item.sprint !== null && item.sprint !== undefined) {
          const sprintId = typeof item.sprint === 'object' ? item.sprint._id : item.sprint;
          if (!sprintId || (typeof sprintId === 'string' && sprintId.trim() === '')) {
            entryErrors.push(`Invalid sprint ID`);
          }
        }

        // Tag validation (optional but if exists, should be valid)
        if (item.tag !== null && item.tag !== undefined) {
          const tagId = typeof item.tag === 'object' ? item.tag._id : item.tag;
          if (!tagId || (typeof tagId === 'string' && tagId.trim() === '')) {
            entryErrors.push(`Invalid tag ID`);
          }
        }

        // hr, min, sec validation
        if (duration) {
          const expectedHr = Math.floor(duration / 3600);
          const expectedMin = Math.floor((duration % 3600) / 60);
          const expectedSec = Math.floor(duration % 60);

          if (item.hr !== undefined && item.hr !== null && item.hr !== expectedHr) {
            entryErrors.push(`Hours mismatch (expected: ${expectedHr}, got: ${item.hr})`);
          }

          if (item.min !== undefined && item.min !== null && item.min !== expectedMin) {
            entryErrors.push(`Minutes mismatch (expected: ${expectedMin}, got: ${item.min})`);
          }

          if (item.sec !== undefined && item.sec !== null && item.sec !== expectedSec) {
            entryErrors.push(`Seconds mismatch (expected: ${expectedSec}, got: ${item.sec})`);
          }
        }

        // totalHours validation (can be string or number)
        const totalHours = typeof item.totalHours === 'string' ? parseFloat(item.totalHours) : item.totalHours;
        if (totalHours === undefined || totalHours === null || (typeof totalHours !== 'number' || isNaN(totalHours))) {
          entryErrors.push(`Invalid or missing total hours`);
        }

        if (entryErrors.length > 0) {
          invalidEntries.push({
            index: index + 1,
            task: item.task || 'Unknown',
            _id: item._id || 'No ID',
            errors: entryErrors
          });
          validationErrors.push(...entryErrors.map(err => `Entry ${index + 1} (${item.task || 'Unknown'}): ${err}`));
        }
      });

      // If validation errors exist, show detailed error message
      if (validationErrors.length > 0) {
        const errorMessage = `Validation failed for ${invalidEntries.length} entry/entries:\n\n${invalidEntries.slice(0, 5).map(entry =>
          `Entry ${entry.index} (${entry.task}):\n${entry.errors.map(e => `  - ${e}`).join('\n')}`
        ).join('\n\n')}${invalidEntries.length > 5 ? `\n\n... and ${invalidEntries.length - 5} more entries with errors.` : ''}`;

        console.error("Validation errors:", validationErrors);
        console.error("Invalid entries:", invalidEntries);

        // Store invalid entry IDs for highlighting
        const invalidIds = new Set(invalidEntries.map(entry => entry._id));
        setInvalidEntryIds(invalidIds);

        toast.error(`Please fix validation errors in selected entries before importing:\n\n${invalidEntries.slice(0, 3).map(entry =>
          `Entry ${entry.index}: ${entry.errors[0]}`
        ).join('\n')}${invalidEntries.length > 3 ? `\n... and ${invalidEntries.length - 3} more` : ''}`, {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setInsertionLoading(false);
        return;
      }

      // Clear invalid entry IDs if validation passes
      setInvalidEntryIds(new Set());

      // Use the calendar-specific API endpoint
      const response = await axios.post(
        "/api/time-track/importdata/insert/calendar-import",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { insertedCount, deletedCount } = response.data;

      toast.success(`Successfully inserted ${insertedCount} entries.`, {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      refetchTimerData();
      props.onCancel();
    } catch (error) {
      console.error("Error in handleInsertAndClear:", error);
      global.config.slackMessage(error.toString());

      // Show more detailed error message from API if available
      const errorMessage = error.response?.data?.message || error.message || "Failed to insert and clear data. Please try again.";

      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setInsertionLoading(false);
    }
  };

  const handleClearImportData = async () => {
    try {
      setCancellationLoading(true);

      const response = await axios.post(
        "/api/time-track/clear/importdata/timer",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      refetchTimerData();
      props.onCancel();
    } catch (error) {
      console.error("Error in handleInsertAndClear:", error);
      global.config.slackMessage(error.toString());

      toast.error("Failed to insert and clear data. Please try again.", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setCancellationLoading(false);
    }
  };


  function filterAndFindExtremes(dates) {
    const dateObject = moment();
    const formattedDate = dateObject.format("YYYY-MM-DD");
    const currentDate = new Date(formattedDate);
    const currentWeekStartDate = new Date(currentDate);
    const currentWeekEndDate = new Date(currentWeekStartDate);
    if (appSettings.weekStartDay && appSettings.weekStartDay === "Monday") {
      currentWeekStartDate.setDate(
        currentDate.getDate() - ((currentDate.getDay() + 6) % 7)
      );
      currentWeekStartDate.setHours(0, 0, 0, 0);
      const currentWeekEndDate = new Date(currentWeekStartDate);
      const getDateNew = moment(currentWeekEndDate);
      const sixDaysAfter = getDateNew.add(6, "days");
      currentWeekEndDate.setDate(sixDaysAfter.format("YYYY-MM-DD"));
      currentWeekEndDate.setHours(23, 59, 59, 999);
    } else {
      currentWeekStartDate.setDate(
        currentDate.getDate() - currentDate.getDay()
      );
      currentWeekStartDate.setHours(0, 0, 0, 0);
      const currentWeekEndDate = new Date(currentWeekStartDate);
      const getDateNew = moment(currentWeekEndDate);
      const sixDaysAfter = getDateNew.add(6, "days");
      currentWeekEndDate.setDate(sixDaysAfter.format("YYYY-MM-DD"));
      currentWeekEndDate.setHours(23, 59, 59, 999);
    }
    const datesInCurrentWeek = dates.filter((date) => {
      return date >= currentWeekStartDate && date <= currentWeekEndDate;
    });

    const highestDate = new Date(Math.max(...datesInCurrentWeek));
    const leastDate = new Date(Math.min(...datesInCurrentWeek));

    return { highestDate, leastDate };
  }

  function findExtremesOfLastWeek(dates) {
    const currentDate = new Date();
    const dateObject = moment();
    const formattedDate = dateObject.format("YYYY-MM-DD");
    const lastWeekStartDate = new Date(formattedDate);

    if (appSettings.weekStartDay && appSettings.weekStartDay === "Monday") {
      lastWeekStartDate.setDate(
        currentDate.getDate() - ((currentDate.getDay() + 6) % 7) - 7
      );
    } else {
      lastWeekStartDate.setDate(
        currentDate.getDate() - currentDate.getDay() - 7
      );
    }

    lastWeekStartDate.setHours(0, 0, 0, 0);
    const lastWeekEndDate = new Date(lastWeekStartDate);
    lastWeekEndDate.setDate(lastWeekStartDate.getDate() + 6);
    lastWeekEndDate.setHours(23, 59, 59, 999);

    const datesInLastWeek = dates.filter((date) => {
      return date >= lastWeekStartDate && date <= lastWeekEndDate;
    });

    const highestLastWeekDate = new Date(Math.max(...datesInLastWeek));
    const leastLastWeekDate = new Date(Math.min(...datesInLastWeek));
    highestLastWeekDate.setHours(0, 0, 0, 0);
    leastLastWeekDate.setHours(0, 0, 0, 0);
    return { highestLastWeekDate, leastLastWeekDate };
  }

  function findExtremesByWeek(dates) {
    const datesByWeek = {};
    for (const date of dates) {
      const weekStartDatefil = moment(date);
      const weekStartDatef = weekStartDatefil.format("YYYY-MM-DD");
      const weekStartDate = new Date(weekStartDatef);
      if (appSettings.weekStartDay && appSettings.weekStartDay === "Monday") {
        weekStartDate.setDate(date.getDate() - ((date.getDay() + 6) % 7));
        weekStartDate.setHours(0, 0, 0, 0);
      } else {
        weekStartDate.setDate(date.getDate() - date.getDay());
        weekStartDate.setHours(0, 0, 0, 0);
      }
      const weekStartDateString = weekStartDate.toISOString();
      if (!datesByWeek[weekStartDateString]) {
        datesByWeek[weekStartDateString] = [];
      }
      datesByWeek[weekStartDateString].push(date);
    }
    const extremesByWeek = [];
    for (const weekDates of Object.values(datesByWeek)) {
      const highestWeekDate = new Date(Math.max(...weekDates));
      const leastDate = new Date(Math.min(...weekDates));
      extremesByWeek.push({ highestWeekDate, leastDate });
    }
    return extremesByWeek;
  }

  const handlePageChange = (selected) => {
    setSelectedPage(selected.selected);
  };

  const totalResults = timer.length;
  const pageCount = Math.ceil(timer.length / perPage);
  const offset = perPage * selectedPage;
  const currentPageData = timer.slice(offset, offset + perPage);
  const { highestDate, leastDate } = filterAndFindExtremes(
    currentPageData.map((item) => new Date(item[0]))
  );
  const { highestLastWeekDate, leastLastWeekDate } = findExtremesOfLastWeek(
    currentPageData.map((item) => new Date(item[0]))
  );
  const extremesByWeek = findExtremesByWeek(
    currentPageData.map((item) => new Date(item[0]))
  );

  function getMomentDate(date, format) {
    const dateObject = moment(date, format);
    return dateObject.format("YYYY-MM-DD");
  }

  const findLeastDate = (inputDate, data) => {
    let leastDate = null;
    data.forEach((item) => {
      const itemDate = new Date(item.highestWeekDate);
      const startDate = new Date(item.leastDate);
      const endDate = new Date(item.highestWeekDate);
      if (inputDate >= startDate && inputDate <= endDate) {
        leastDate = item.leastDate;
      }
    });
    return leastDate;
  };

  const findExtremeDate = (inputDate, data) => {
    let leastDate = null;
    let highestDate = null;

    data.forEach((item) => {
      const itemDate = new Date(item.highestWeekDate);
      const startDate = new Date(item.leastDate);
      const endDate = new Date(item.highestWeekDate);
      if (inputDate >= startDate && inputDate <= endDate) {
        leastDate = item.leastDate;
        highestDate = item.highestWeekDate;
      }
    });
    return highestDate;
  };

  const toggleCalendar = () => {
    if (!isFromCalendar) {
      setShowCalendar(!showCalendar);
    }
  };

  const cancelCalendar = () => {
    setShowCalendar(false);
  };

  const onDateChange = (ranges) => {
    try {
      if (!ranges || !ranges.startDate || !ranges.endDate) {
        // fallback to current month if ranges is null or invalid
        const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        setDateRange([firstDayOfMonth, new Date()]);
      } else {
        const start = new Date(ranges.startDate);
        const end = new Date(ranges.endDate);

        // Validate parsed dates
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          throw new Error("Invalid date range provided.");
        }

        const startOfDayUTC = moment(start).startOf("day").toDate();
        const endOfDayUTC = moment(end).endOf("day").toDate();
        setDateRange([startOfDayUTC, endOfDayUTC]);
      }
    } catch (error) {
      console.error("Error in onDateChange:", error);

      // Fallback to current month
      const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      setDateRange([firstDayOfMonth, new Date()]);
    } finally {
      setShowCalendar(false);
    }
  };

  // Handle click outside calendar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Helper function to format date range
  const formatDateRange = (dateRange) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return null;
    const formatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };
    return `${new Date(dateRange[0]).toLocaleDateString("en-GB", formatOptions)} to ${new Date(dateRange[1]).toLocaleDateString("en-GB", formatOptions)}`;
  };

  // Helper function to render week total header
  const renderWeekTotalHeader = (label, startDate, endDate, useThisWeek = false) => {
    if (!timer.length) return null;

    return (
      <div className="total-time-tracked">
        <div className="total-time-date-left">{label}</div>
        <div className="total-time-date-right">
          Week Total:
          {useThisWeek ? (
            <ThisWeekTotalDisplay
              startDate={startDate}
              endDate={endDate}
              selectedUser={selectedUser}
            />
          ) : (
            <WeekTotalDisplay
              startDate={startDate}
              endDate={endDate}
              selectedUser={selectedUser}
            />
          )}
        </div>
      </div>
    );
  };

  // Helper function to render other week totals
  const renderOtherWeekTotal = (itemDate) => {
    const itemDateString = new Date(itemDate).toDateString();
    const isCurrentWeek = itemDateString === highestDate.toDateString();
    const isLastWeek = itemDateString === highestLastWeekDate.toDateString();

    if (isCurrentWeek || isLastWeek) return null;

    const isWeekExtreme = extremesByWeek.some(
      (extreme) => extreme.highestWeekDate.toDateString() === itemDateString
    );

    if (!isWeekExtreme) return null;

    const leastDateInWeek = findLeastDate(new Date(itemDate), extremesByWeek);
    const highestDateInWeek = findExtremeDate(new Date(itemDate), extremesByWeek);

    if (!leastDateInWeek || !highestDateInWeek) return null;

    // Ensure dates are Date objects
    const leastDateObj = leastDateInWeek instanceof Date ? leastDateInWeek : new Date(leastDateInWeek);
    const highestDateObj = highestDateInWeek instanceof Date ? highestDateInWeek : new Date(highestDateInWeek);

    const startMoment = moment(getMomentDate(leastDateObj, "ddd MMM DD YYYY"))
      .tz(global.config.getTimeZone());
    const endMoment = moment(getMomentDate(highestDateObj.toDateString(), "ddd MMM DD YYYY"))
      .tz(global.config.getTimeZone());

    return (
      <div className="total-time-tracked">
        <div className="total-time-date-left">
          {`${startMoment.format("MMM D")} - ${endMoment.format("MMM D")}`}
          {new Date(itemDate).getFullYear() !== new Date().getFullYear() && (
            <span>, {new Date(itemDate).getFullYear()}</span>
          )}
        </div>
        <div className="total-time-date-right">
          Week Total:
          <WeekTotalDisplay
            selectedUser={selectedUser}
            startDate={leastDateObj.toDateString()}
            endDate={endMoment.format("MMM DD YYYY")}
          />
        </div>
      </div>
    );
  };

  // Pagination options
  const paginationOptions = [10, 20, 30, 40].filter(option => totalResults >= option);

  return (
    <motion.div
      ref={componentRef}
      initial="hidden"
      animate="visible"
      variants={fadeIn}
      transition={{ duration: 0.18 }}
    >
      <div className="time-tracker-outer time-tracker-outer-import-section time-tracker-outer-import-section-new">
        <div className="import-tracker-top-section">
          <h2>Calendar Import</h2>
          <div className="cancel-import-tracker">
            <button
              className="cancelbtn-link"
              disabled={cancellationLoading}
              onClick={cancellationLoading ? undefined : handleClearImportData}
              style={{ 
                pointerEvents: cancellationLoading ? "none" : "auto",
                cursor: cancellationLoading ? "default" : "pointer",
                opacity: cancellationLoading ? 0.7 : 1
              }}
            >
              {cancellationLoading ? (
                <>
                  <div
                    style={{
                      width: "10.5px",
                      height: "10.5px",
                      backgroundColor: "#282828",
                      borderRadius: "2px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                  <div
                    style={{
                      width: "60px",
                      height: "17px",
                      backgroundColor: "#282828",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </>
              ) : (
                <>
                  <img src={cancelImage} alt="cancel" /> Cancel
                </>
              )}
            </button>
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="filter-inner due-date" style={{ marginBottom: '20px', marginTop: '20px' }}>
          <label htmlFor="dueDate" className="calendar-icon">
            Date Range
          </label>
          <div
            className="date-picker-outer date-picker-outer-all"
            ref={calendarRef}
          >
            <div className="custoom-icon-calender custoom-icon-calender-all">
              <div className="custoom-icon-calender custoom-icon-calender-all custom-date-range ">
                <input
                  type="text"
                  value={formatDateRange(dateRange) || ''}
                  id="invoiceId"
                  name="invoiceId"
                  placeholder="All Time"
                  onClick={isFromCalendar ? undefined : toggleCalendar}
                  autoComplete="off"
                  readOnly
                  disabled={isFromCalendar}
                  style={{
                    cursor: isFromCalendar ? 'not-allowed' : 'pointer',
                    opacity: isFromCalendar ? 1 : 1,
                    backgroundColor: isFromCalendar ? '#f5f5f5' : 'white'
                  }}
                />
              </div>
              <div className="date-range-new right-align">
                {showCalendar && !isFromCalendar && (
                  <Calendar
                    onCancel={cancelCalendar}
                    onChange={(range) => {
                      if (!range || !range.startDate || !range.endDate) return;
                      onDateChange({
                        startDate: new Date(range.startDate),
                        endDate: new Date(range.endDate),
                      });
                    }}
                    initialRange={{
                      startDate: dateRange ? new Date(dateRange[0]) : null,
                      endDate: dateRange ? new Date(dateRange[1]) : null,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <ToastContainer />
        <div className="tracker-data-import">
          {isTimerDataLoading ? (
            <>
              {/* Skeleton for week total header section - Top */}
              <div className="total-time-tracked">
                <div className="total-time-date-left">
                  <div
                    style={{
                      width: "120px",
                      height: "18px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
                <div className="total-time-date-right">
                  <div
                    style={{
                      width: "90px",
                      height: "18px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>

              {/* Skeleton for timer cards - First group */}
              {Array.from({ length: 3 }).map((_, index) => (
                <Card
                  key={`skeleton-card-${index}`}
                  className="text-center card-bg"
                  style={{ marginBottom: "25px" }}
                >
                  {/* Card Header Skeleton */}
                  <Card.Header>
                    <Row className="card-header-sec">
                      <Col className="card-header-date">
                        <div
                          style={{
                            width: "120px",
                            height: "27px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </Col>
                      <div className="separation-head"></div>
                      <Col className="card-timer">
                        <div
                          style={{
                            width: "80px",
                            height: "27px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </Col>
                    </Row>
                  </Card.Header>
                  {/* Card Body Skeleton */}
                  <Card.Body className="body-mg">
                    <div className="timetracker-inner data-time-tracker overview-sec-timer">
                      <div className="merge-tracker">
                        <div className="overview-sec-timer-inner">
                          <div className="project-name">
                            <Col>
                              <div
                                style={{
                                  width: "200px",
                                  height: "20px",
                                  backgroundColor: "#e5e7eb",
                                  borderRadius: "4px",
                                  animation: "pulse 1.5s ease-in-out infinite",
                                }}
                              />
                            </Col>
                          </div>
                          <div className="project-sprint-outer col">
                            <div
                              style={{
                                width: "150px",
                                height: "20px",
                                backgroundColor: "#e5e7eb",
                                borderRadius: "4px",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </div>
                        </div>
                        <div className="outer-right-sec-overview">
                          <div className="overview-sec-timer-inner-right">
                            <div className="tag-section-mobile-section">
                              <div className="tag-section">
                                <div
                                  style={{
                                    width: "80px",
                                    height: "35px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "100px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </div>
                              <div className="billable-section">
                                <div
                                  style={{
                                    width: "45px",
                                    height: "31px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "100px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </div>
                            </div>
                            <div className="overview-sec-timer-date-time">
                              <Col>
                                <div
                                  style={{
                                    width: "140px",
                                    height: "20px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </Col>
                            </div>
                            <div className="overview-time-edit">
                              <Col>
                                <div className="overview-time-edit-inner">
                                  <div
                                    style={{
                                      width: "70px",
                                      height: "20px",
                                      backgroundColor: "#e5e7eb",
                                      borderRadius: "4px",
                                      animation: "pulse 1.5s ease-in-out infinite",
                                    }}
                                  />
                                </div>
                              </Col>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}

              {/* Skeleton for week total header section */}
              <div className="total-time-tracked">
                <div className="total-time-date-left">
                  <div
                    style={{
                      width: "120px",
                      height: "18px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
                <div className="total-time-date-right">
                  <div
                    style={{
                      width: "90px",
                      height: "18px",
                      backgroundColor: "#e5e7eb",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>

              {/* Skeleton for timer cards - Second group */}
              {Array.from({ length: 3 }).map((_, index) => (
                <Card
                  key={`skeleton-card-${index + 3}`}
                  className="text-center card-bg"
                  style={{ marginBottom: "25px" }}
                >
                  {/* Card Header Skeleton */}
                  <Card.Header>
                    <Row className="card-header-sec">
                      <Col className="card-header-date">
                        <div
                          style={{
                            width: "120px",
                            height: "27px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </Col>
                      <div className="separation-head"></div>
                      <Col className="card-timer">
                        <div
                          style={{
                            width: "80px",
                            height: "27px",
                            backgroundColor: "#e5e7eb",
                            borderRadius: "4px",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                      </Col>
                    </Row>
                  </Card.Header>
                  {/* Card Body Skeleton */}
                  <Card.Body className="body-mg">
                    <div className="timetracker-inner data-time-tracker overview-sec-timer">
                      <div className="merge-tracker">
                        <div className="overview-sec-timer-inner">
                          <div className="project-name">
                            <Col>
                              <div
                                style={{
                                  width: "200px",
                                  height: "20px",
                                  backgroundColor: "#e5e7eb",
                                  borderRadius: "4px",
                                  animation: "pulse 1.5s ease-in-out infinite",
                                }}
                              />
                            </Col>
                          </div>
                          <div className="project-sprint-outer col">
                            <div
                              style={{
                                width: "150px",
                                height: "20px",
                                backgroundColor: "#e5e7eb",
                                borderRadius: "4px",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </div>
                        </div>
                        <div className="outer-right-sec-overview">
                          <div className="overview-sec-timer-inner-right">
                            <div className="tag-section-mobile-section">
                              <div className="tag-section">
                                <div
                                  style={{
                                    width: "80px",
                                    height: "35px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "100px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </div>
                              <div className="billable-section">
                                <div
                                  style={{
                                    width: "45px",
                                    height: "31px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "100px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </div>
                            </div>
                            <div className="overview-sec-timer-date-time">
                              <Col>
                                <div
                                  style={{
                                    width: "140px",
                                    height: "20px",
                                    backgroundColor: "#e5e7eb",
                                    borderRadius: "4px",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                              </Col>
                            </div>
                            <div className="overview-time-edit">
                              <Col>
                                <div className="overview-time-edit-inner">
                                  <div
                                    style={{
                                      width: "70px",
                                      height: "20px",
                                      backgroundColor: "#e5e7eb",
                                      borderRadius: "4px",
                                      animation: "pulse 1.5s ease-in-out infinite",
                                    }}
                                  />
                                </div>
                              </Col>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </>
          ) : timer.length > 0 ? (
            currentPageData.map((item, index) => {
              const itemDateString = new Date(item[0]).toDateString();
              const isCurrentWeek = itemDateString === highestDate.toDateString();
              const isLastWeek = itemDateString === highestLastWeekDate.toDateString();

              return (
                <div key={index} className="tracker-data-import-inner">
                  {isCurrentWeek && renderWeekTotalHeader(
                    "This Week",
                    leastDate.toDateString(),
                    highestDate.toDateString(),
                    true
                  )}

                  {isLastWeek && renderWeekTotalHeader(
                    "Last Week",
                    getMomentDate(leastLastWeekDate, "ddd MMM DD YYYY"),
                    getMomentDate(highestLastWeekDate, "ddd MMM DD YYYY"),
                    false
                  )}

                  {renderOtherWeekTotal(item[0])}

                  <TimeCardImportDataCalendar
                    index={index}
                    item={item}
                    timeFormat={appSettings.timeFormat}
                    refetchTimerData={refetchTimerData}
                    invalidEntryIds={invalidEntryIds}
                    setInvalidEntryIds={setInvalidEntryIds}
                    globalEditedId={editedId}
                    setGlobalEditedId={setEditedId}
                    onEditedIdChange={(id, formRef) => {
                      setEditedId(id);
                      if (formRef) {
                        updateFormRef.current = formRef.current;
                      } else {
                        updateFormRef.current = null;
                      }
                    }}
                  />
                </div>
              );
            })
          ) : (
            <div className="no-result-text">No Results</div>
          )}
        </div>
        <div className="pagination-outer">
          <div className="showing-count">
            {!isTimerDataLoading && timer.length > 0 && (
              <div className="filter-inner showing-result">
                <label>Showing</label>
                <div
                  ref={showingDropdownRef}
                  className="select-modal-mainM custom-dropdown-user-new-outer"
                >
                  <div
                    className="selected-option custom-dropdown-user-new"
                    onClick={() => setShowingDropdownOpen(!showingDropdownOpen)}
                  >
                    {totalResults < 10 ? totalResults.toString() : perPage.toString()}
                  </div>
                  {showingDropdownOpen && (
                    <div className="custom-dropdown-new">
                      <ul className="showing-options-list">
                        {paginationOptions.map((option) => (
                          <li
                            key={option}
                            className={perPage === option ? "selected-item" : ""}
                            onClick={() => {
                              setPerPage(option);
                              setSelectedPage(0);
                              setShowingDropdownOpen(false);
                            }}
                          >
                            {option}
                          </li>
                        ))}
                        {totalResults >= 10 && (
                          <li
                            className={perPage === totalResults ? "selected-item" : ""}
                            onClick={() => {
                              setPerPage(totalResults);
                              setSelectedPage(0);
                              setShowingDropdownOpen(false);
                            }}
                          >
                            ALL
                          </li>
                        )}
                        {totalResults < 10 && (
                          <li
                            className="selected-item"
                            onClick={() => {
                              setPerPage(totalResults);
                              setSelectedPage(0);
                              setShowingDropdownOpen(false);
                            }}
                          >
                            {totalResults}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <label>of {totalResults} Results</label>
              </div>
            )}
          </div>
          <div className="pagination" style={{ position: "relative", minHeight: "40px" }}>
            {pageCount > 1 && (
              <>
                {isTimerDataLoading ? (
                  <ul className="pagination maininteampag pagination-loading">
                    {/* Skeleton for Back button - Only show if there would be a previous page */}
                    {selectedPage > 0 && (
                      <li className="previous">
                        <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                          &lt; Back
                        </a>
                      </li>
                    )}
                    {/* Skeleton for page numbers - Show first page, current page area, and last page */}
                    <li>
                      <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                        1
                      </a>
                    </li>
                    {pageCount > 2 && (
                      <>
                        {selectedPage > 1 && (
                          <li className="break-me">
                            <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                              ...
                            </a>
                          </li>
                        )}
                        {selectedPage > 0 && selectedPage < pageCount - 1 && (
                          <li className="active">
                            <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                              {selectedPage + 1}
                            </a>
                          </li>
                        )}
                        {selectedPage < pageCount - 2 && (
                          <li className="break-me">
                            <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                              ...
                            </a>
                          </li>
                        )}
                      </>
                    )}
                    {pageCount > 1 && (
                      <li>
                        <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                          {pageCount}
                        </a>
                      </li>
                    )}
                    {/* Skeleton for Next button - Only show if there would be a next page */}
                    {selectedPage < pageCount - 1 && (
                      <li className="next">
                        <a style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}>
                          Next &gt;
                        </a>
                      </li>
                    )}
                  </ul>
                ) : (
                  <div
                    style={{
                      pointerEvents: isTimerDataLoading ? "none" : "auto",
                      opacity: isTimerDataLoading ? 0.5 : 1,
                      transition: "opacity 0.2s ease-in-out"
                    }}
                  >
                    <ReactPaginate
                      previousLabel={selectedPage > 0 ? "< Back" : null}
                      nextLabel={selectedPage < pageCount - 1 ? "Next >" : null}
                      breakLabel={"..."}
                      breakClassName={"break-me"}
                      pageCount={pageCount}
                      marginPagesDisplayed={1}
                      pageRangeDisplayed={1}
                      onPageChange={handlePageChange}
                      containerClassName={`pagination maininteampag ${isTimerDataLoading ? "pagination-loading" : ""}`}
                      activeClassName={"active"}
                      forcePage={selectedPage}
                      disableInitialCallback={true}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="confirm-import">
          {editedId ? (
            <button
              className="button-confirm"
              disabled={insertionLoading}
              onClick={
                insertionLoading
                  ? undefined
                  : () => {
                      if (updateFormRef && updateFormRef.current) {
                        // Use requestSubmit if available, otherwise use submit
                        if (
                          typeof updateFormRef.current.requestSubmit ===
                          "function"
                        ) {
                          updateFormRef.current.requestSubmit();
                        } else {
                          updateFormRef.current.submit();
                        }
                      }
                    }
              }
              style={{
                pointerEvents: insertionLoading ? "none" : "auto",
                cursor: insertionLoading ? "default" : "pointer",
                opacity: insertionLoading ? 0.7 : 1,
              }}
            >
              {insertionLoading ? (
                <>
                  <div
                    style={{
                      width: "18px",
                      height: "18px",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                  <div
                    style={{
                      width: "60px",
                      height: "17px",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </>
              ) : (
                <>
                  <img src={tick} alt="tick" /> Update
                </>
              )}
            </button>
          ) : (
            <button
              className="button-confirm"
              disabled={insertionLoading}
              onClick={insertionLoading ? undefined : handleInsertAndClear}
              style={{
                pointerEvents: insertionLoading ? "none" : "auto",
                cursor: insertionLoading ? "default" : "pointer",
                opacity: insertionLoading ? 0.7 : 1,
              }}
            >
              {insertionLoading ? (
                <>
                  <div
                    style={{
                      width: "18px",
                      height: "18px",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                  <div
                    style={{
                      width: "200px",
                      height: "17px",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "4px",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </>
              ) : (
                <>
                  <img src={tick} alt="tick" /> Import Selected Events ({selectedEntriesCount || 0})
                </>
              )}
            </button>
          )}
        </div>
      </div>
      {/* Pulse animation style */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default TrackerImportDataCalendar;
